import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaMoneyBillWave, FaUniversity, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import './Transactions.css';

const banks = [
  { key: 'CBE', label: 'CBE' },
  { key: 'Awash', label: 'Awash' },
  { key: 'Dashen', label: 'Dashen' },
  { key: 'Abyssinia', label: 'Abyssinia' },
  { key: 'Birhan', label: 'Birhan' },
  { key: 'Telebirr', label: 'Telebirr' },
  { key: 'Check', label: 'Check' },
];

const Transactions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paidAmounts, setPaidAmounts] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({});
  const [selectedBanks, setSelectedBanks] = useState({});
  const [partialAmounts, setPartialAmounts] = useState({});
  const navigate = useNavigate();

  const updatePartialAmount = (transactionId, type, amount) => {
    setPartialAmounts(prev => ({
      ...prev,
      [transactionId]: {
        ...(prev[transactionId] || {}),
        [type]: amount
      }
    }));
  };

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${ENDPOINT}?action=getNewTransactions`,
        {},
        {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const transactionsData = response.data.transactions;
        setTransactions(transactionsData);
        
        // Initialize payment methods and banks
        const initialMethods = {};
        const initialBanks = {};
        transactionsData.forEach(tx => {
          initialMethods[tx.id] = tx.payment_method || 'cash';
          initialBanks[tx.id] = 'CBE';
        });
        setPaymentMethods(initialMethods);
        setSelectedBanks(initialBanks);
      } else {
        throw new Error(response.data.message || 'Failed to fetch transactions');
      }
    } catch (err) {
      setError(`Failed to load transactions: ${err.message}`);
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (amount) => `ETB ${Number(amount).toFixed(2)}`;

  const calculateTransactionTotal = (items) => 
    items.reduce((total, item) => total + item.quantity * item.unit_price, 0);

  const updatePaidAmount = (transactionId, text) => {
    setPaidAmounts(prev => ({ ...prev, [transactionId]: text }));
  };

  const updatePaymentMethod = (transactionId, method) => {
    setPaymentMethods(prev => ({ ...prev, [transactionId]: method }));
  };

  const updateSelectedBank = (transactionId, bank) => {
    setSelectedBanks(prev => ({ ...prev, [transactionId]: bank }));
  };

  const handlePay = async (transactionId) => {
    const paymentMethod = paymentMethods[transactionId] || 'cash';
    let paidAmount = paidAmounts[transactionId] || '0';
    let cashAmount = '0';
    let bankAmount = '0';
    let bankName = '';

    if (paymentMethod === 'partial') {
      cashAmount = partialAmounts[transactionId]?.cash || '0';
      bankAmount = partialAmounts[transactionId]?.bank || '0';
      bankName = selectedBanks[transactionId] || '';
      
      if ((!cashAmount || parseFloat(cashAmount) <= 0) && (!bankAmount || parseFloat(bankAmount) <= 0)) {
        toast.error('Please enter valid payment amounts for either cash or bank.');
        return;
      }
      
      if (bankAmount > 0 && !bankName) {
        toast.error('Please select a bank for the bank payment.');
        return;
      }
      
      paidAmount = (parseFloat(cashAmount || 0) + parseFloat(bankAmount || 0)).toString();
    } else {
      if (!paidAmount || isNaN(paidAmount) || parseFloat(paidAmount) <= 0) {
        toast.error('Please enter a valid payment amount.');
        return;
      }
      
      if (paymentMethod === 'bank') {
        bankName = selectedBanks[transactionId] || '';
        if (!bankName) {
          toast.error('Please select a bank for the payment.');
          return;
        }
      }
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${ENDPOINT}?action=payTransaction`,
        {
          transaction_id: transactionId,
          paid_amount: parseFloat(paidAmount),
          payment_method: paymentMethod,
          bank_name: bankName
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success('Payment successful!');
        fetchTransactions();
      } else {
        throw new Error(response.data.message || 'Failed to process payment');
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast.error(`Payment failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const renderTransaction = (transaction) => {
    const total = calculateTransactionTotal(transaction.items);
    const paymentMethod = paymentMethods[transaction.id] || 'cash';
    const isBankPayment = paymentMethod === 'bank';
    const isPartialPayment = paymentMethod === 'partial';

    return (
      <div key={transaction.id} className="transaction-card">
        <div className="transaction-header">
          <h3 className="customer-name">{transaction.customer_name || 'Walk-in Customer'}</h3>
          <span className="transaction-date">{formatDate(transaction.transaction_date)}</span>
        </div>

        <div className="transaction-items">
          {transaction.items.map((item, index) => (
            <div key={index} className="item-row">
              <span className="item-name">{`• ${item.product_name || `Product ID: ${item.product_id}`}`}</span>
              <span className="item-details">{`${item.quantity} x ${formatCurrency(item.unit_price)}`}</span>
            </div>
          ))}
        </div>

        <div className="transaction-totals">
          <div className="total-row">
            <span>Total</span>
            <span className="total-amount">{formatCurrency(total)}</span>
          </div>
          {transaction.unpaid_amount > 0 && (
            <div className="total-row">
              <span>Unpaid</span>
              <span className="unpaid-amount">{formatCurrency(transaction.unpaid_amount)}</span>
            </div>
          )}
        </div>

        <div className="payment-section">
          <div className="payment-methods">
            <button 
              className={`payment-method-btn ${paymentMethod === 'cash' ? 'active' : ''}`}
              onClick={() => updatePaymentMethod(transaction.id, 'cash')}
            >
              <FaMoneyBillWave className="payment-icon" />
              <span>Cash</span>
            </button>
            <button 
              className={`payment-method-btn ${isBankPayment ? 'active' : ''}`}
              onClick={() => updatePaymentMethod(transaction.id, 'bank')}
            >
              <FaUniversity className="payment-icon" />
              <span>Bank</span>
            </button>
          </div>

          {(isBankPayment || isPartialPayment) && (
            <div className="bank-selector">
              <label>Bank:</label>
              <select 
                className="bank-select"
                value={selectedBanks[transaction.id] || ''}
                onChange={(e) => updateSelectedBank(transaction.id, e.target.value)}
              >
                <option value="">Select a bank...</option>
                {banks.map(bank => (
                  <option key={bank.key} value={bank.key}>
                    {bank.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {isPartialPayment ? (
            <div className="partial-payment">
              <div className="form-group">
                <label>Cash:</label>
                <input
                  type="number"
                  className="payment-input"
                  placeholder="Cash amount"
                  value={partialAmounts[transaction.id]?.cash || ''}
                  onChange={(e) => updatePartialAmount(transaction.id, 'cash', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Bank:</label>
                <input
                  type="number"
                  className="payment-input"
                  placeholder="Bank amount"
                  value={partialAmounts[transaction.id]?.bank || ''}
                  onChange={(e) => updatePartialAmount(transaction.id, 'bank', e.target.value)}
                />
              </div>
              <button 
                className="pay-btn"
                onClick={() => handlePay(transaction.id)}
              >
                Pay
              </button>
            </div>
          ) : (
            <div className="payment-input-group">
              <input
                type="number"
                className="payment-input"
                placeholder="Enter payment amount"
                value={paidAmounts[transaction.id] || ''}
                onChange={(e) => updatePaidAmount(transaction.id, e.target.value)}
              />
              <button 
                className="pay-btn"
                onClick={() => handlePay(transaction.id)}
              >
                Pay
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading Transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">
          <FaExclamationTriangle />
        </div>
        <p className="error-message">{error}</p>
        <button className="retry-btn" onClick={fetchTransactions}>
          <FaSync className="retry-icon" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="transactions-container">
      <div className="transactions-header">
        <h2>Transactions</h2>
        <button className="refresh-btn" onClick={fetchTransactions}>
          <FaSync />
          Refresh
        </button>
      </div>
      
      {transactions.length === 0 ? (
        <div className="empty-state">
          <p>No transactions found.</p>
        </div>
      ) : (
        <div className="transactions-list">
          {transactions.map(transaction => renderTransaction(transaction))}
        </div>
      )}
    </div>
  );
};

export default Transactions;
